import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Phone, Mail, ArrowRight } from "lucide-react";
import { useLogout } from "@/hooks/useLogout";
import Loader from "@/components/ui/loader";
import PendingApprovalLayout from "@/components/layout/auth/PendingApprovalLayout";
import { useI18n } from "@/hooks/useI18n";

const PendingApproval = () => {
  const { user, userRole } = useAuth();
  const { logout, isLoggingOut } = useLogout();
  const { t } = useI18n();

  const handleLogout = () => {
    logout('/login');
  };

  const getRoleDisplayName = () => {
    switch (userRole) {
      case 'travel_designer':
        return t('role.travelAdvisor');
      case 'travel_advisor_member':
        return t('role.travelAdvisorMember');
      case 'admin':
        return t('role.admin');
      case 'traveler':
        return t('role.traveler');
      default:
        return t('role.user');
    }
  };

  return (
    <PendingApprovalLayout>

      {/* Main Card */}
      <Card className="border border-gray-200 rounded-2xl">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <img
              src="/assets/images/dark_extended.png"
              alt="Place2Be Logo"
              className="w-48 h-13 object-contain drop-shadow-lg"
            />
          </div>
          <CardTitle className="text-2xl text-gray-900 flex items-center justify-center">
            <Clock className="h-6 w-6 mr-3" />Account in Attesa di Approvazione
          </CardTitle>
          <CardDescription className="text-base text-gray-600">
            Il tuo account è attualmente in fase di revisione da parte del nostro team
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">

          {/* User Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-center">
              <p className="text-gray-500 mb-1">Ruolo Richiesto</p>
              <p className="font-semibold text-gray-900">{getRoleDisplayName()}</p>
              {user?.email && (
                <>
                  <p className="text-gray-500 mt-2 mb-1">Email</p>
                  <p className="text-gray-700">{user.email}</p>
                </>
              )}
            </div>
          </div>

          {/* What happens next */}
          <div className="space-y-4">
            <h2 className="text-gray-900 text-center">
              Cosa succede ora?
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-600">
                  Il nostro team esaminerà la tua richiesta entro 24-48 ore
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-600">
                  Riceverai una notifica via email una volta completata la revisione
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <p className="text-gray-600">
                  Dopo l'approvazione, avrai accesso completo a tutte le funzionalità
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h2 className="text-blue-900 mb-3 text-center">
              Hai bisogno di assistenza?
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-3">
                <Phone className="h-4 w-4 text-blue-600" />
                <span className="text-blue-800">+39 123 456 7890</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Mail className="h-4 w-4 text-blue-600" />
                <span className="text-blue-800">support@place2be.com</span>
              </div>
            </div>
            <p className="text-sm text-blue-700 text-center mt-3">
              Il nostro team di supporto è disponibile dal lunedì al venerdì, 9:00-18:00
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              onClick={handleLogout}
              disabled={isLoggingOut}
              variant="outline"
              className="flex-1"
            >
              {isLoggingOut ? (
                <div className="flex items-center gap-2">
                  <Loader size="sm" className="mr-3" />
                  Uscendo...
                </div>
              ) : (
                <>
                  Esci dall'Account
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>
            <Button
              onClick={() => window.location.reload()}
              className="flex-1 bg-gray-900 hover:bg-gray-800 text-white"
            >
              Controlla Stato
            </Button>
          </div>
        </CardContent>
      </Card>
    </PendingApprovalLayout>
  );
};

export default PendingApproval; 